import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  NoteAdd as NoteAddIcon,
} from '@mui/icons-material';

import {
  SpreadsheetDocumentsData,
  SpreadsheetDocument,
  SpreadsheetColumn,
  SpreadsheetRow,
  createEmptyDocument,
  documentFromGrid,
  appendGridToDocument,
  SpreadsheetGridInput,
} from '../../models/SpreadsheetDocument';
import { parseExcelWorkbook } from '../../pages/SiteSettingsPage/excelImport';
import { exportDocumentToXlsx, SpreadsheetBatchExportResult } from './exportSpreadsheet';
import SpreadsheetGrid from './SpreadsheetGrid';
import DocumentOrderControl from './DocumentOrderControl';
import FolderSpreadsheetUpload from './FolderSpreadsheetUpload';
import { appendImportedDocuments } from './importSpreadsheetFolder';
import { readWorkbookSheets, WorkbookSheetIssue } from './readWorkbookSheets';
import { importWorkbookFiles, sheetDocumentName, WorkbookFileIssue } from './importWorkbookFiles';
import WorkbookSheetDialog from './WorkbookSheetDialog';
import SpreadsheetBatchDownload from './SpreadsheetBatchDownload';

const DEBOUNCE_MS = 700;

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

/** 업로드 동작 — 새 문서 생성 vs 기존 문서에 append. */
type UploadAction = { mode: 'new' } | { mode: 'append'; docId: string };

interface PendingWorkbook {
  action: UploadAction;
  workbook: import('xlsx').WorkBook;
  sheetNames: string[];
  fileBase: string;
}

interface SpreadsheetDocumentsProps {
  /** 초기 데이터 로드 — projectId 로 memoize 해 전달. */
  load: () => Promise<SpreadsheetDocumentsData>;
  /** 변경 저장(디바운스). projectId 로 memoize 해 전달. */
  save: (data: SpreadsheetDocumentsData) => Promise<void>;
  writable: boolean;
  title: string;
  description?: string;
  /** 새 빈 문서 / seed 시 기본 컬럼(이름·폭). */
  defaultColumns: ReadonlyArray<{ name: string; width?: number }>;
  /** 새 빈 문서 기본 이름. */
  newDocName?: string;
  /** 엑셀 내보내기 파일명 접두어. */
  exportPrefix?: string;
  /** 지정 시 이 이름의 컬럼에 셀 수정일을 자동 기입(계정 정보의 "업데이트 날짜"). */
  stampColumnName?: string;
  /**
   * 업로드한 엑셀 그리드를 문서에 반영하기 전에 변환(예: 필요한 컬럼만 추출).
   * 문자열을 반환하면 그 메시지로 업로드를 거부한다.
   */
  importGrid?: (grid: SpreadsheetGridInput) => SpreadsheetGridInput | string;
  /** 지정 시 문서 내보내기 버튼이 기본 .xlsx 대신 이 함수를 호출한다. */
  exportDoc?: (doc: SpreadsheetDocument) => void | Promise<void>;
  /** 지정 시 읽기 전용 사용자도 사용할 수 있는 전체 ZIP 다운로드 버튼을 표시한다. */
  exportAllDocs?: (documents: SpreadsheetDocument[]) => Promise<SpreadsheetBatchExportResult>;
  /** 폴더의 엑셀을 파일별 새 문서로 일괄 등록하는 버튼을 표시한다. */
  allowFolderUpload?: boolean;
  /** 문서 순서를 드래그 또는 위치 선택으로 변경한다. */
  allowReorder?: boolean;
  /** 여러 시트가 있는 엑셀에서 전체 시트 가져오기를 제공한다. */
  allowAllSheetsImport?: boolean;
  /** 문서 줄의 내보내기 버튼 뒤에 붙일 추가 동작(예: NVR 원격 등록). 없으면 null. */
  renderDocActions?: (doc: SpreadsheetDocument) => React.ReactNode;
}

/** 확장자 제거한 파일 베이스명. */
function fileBaseName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '') || fileName;
}

/**
 * 다중 문서 스프레드시트 — 자재 / IP·네트워크 공용.
 *
 * 각 문서는 제목 토글(아코디언)로 접었다 펼친다. 가져온 시트마다 새 문서를 만든다.
 * 기존 문서에 행 추가(append)도 가능. 제목은 파일명으로 생성되며 수정 가능. 내보내기/삭제 지원.
 * 모든 변경은 디바운스 후 자동 저장.
 */
const SpreadsheetDocuments: React.FC<SpreadsheetDocumentsProps> = ({
  load,
  save,
  writable,
  title,
  description,
  defaultColumns,
  newDocName = '새 문서',
  exportPrefix,
  stampColumnName,
  importGrid,
  exportDoc,
  exportAllDocs,
  allowFolderUpload = false,
  allowReorder = false,
  allowAllSheetsImport = false,
  renderDocActions,
}) => {
  const [data, setData] = useState<SpreadsheetDocumentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [snack, setSnack] = useState<{
    msg: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [folderImporting, setFolderImporting] = useState(false);

  const draggedId = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; after: boolean } | null>(null);

  const moveDocument = useCallback(
    (id: string, position: number, targetId?: string, after = false) => {
      if (!writable || !allowReorder) return;
      setData((previous) => {
        if (!previous) return previous;
        const source = previous.documents.findIndex((doc) => doc.id === id);
        if (source < 0 || targetId === id) return previous;
        const next = [...previous.documents];
        const [document] = next.splice(source, 1);
        const target = targetId ? next.findIndex((doc) => doc.id === targetId) : position;
        if (target < 0) return previous;
        next.splice(Math.min(next.length, target + (targetId && after ? 1 : 0)), 0, document);
        if (next.every((doc, index) => doc === previous.documents[index])) return previous;
        return { ...previous, documents: next };
      });
    },
    [writable, allowReorder],
  );

  // 업로드(엑셀) 상태머신.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadActionRef = useRef<UploadAction>({ mode: 'new' });
  const [pendingWorkbook, setPendingWorkbook] = useState<PendingWorkbook | null>(null);
  const [pendingSheetName, setPendingSheetName] = useState<string>('');
  const [sheetIssues, setSheetIssues] = useState<WorkbookSheetIssue[]>([]);
  // 여러 파일을 한 번에 올릴 때의 진행률 — 파일마다 순서대로 읽는다.
  const [fileProgress, setFileProgress] = useState<{ completed: number; total: number } | null>(
    null,
  );
  const [fileIssues, setFileIssues] = useState<WorkbookFileIssue[]>([]);

  // 자동 저장용 refs — stale 방지 + 재구독 회피.
  const saveRef = useRef(save);
  saveRef.current = save;
  const latestDataRef = useRef<SpreadsheetDocumentsData | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextAutoSaveRef = useRef(true);

  // 로드.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then((fetched) => {
        if (cancelled) return;
        skipNextAutoSaveRef.current = true;
        setData(fetched);
        latestDataRef.current = fetched;
        setExpanded(new Set()); // 기본 전체 접힘 — 제목만 훑고 필요한 문서만 펼친다.
        setSaveStatus('idle');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // 변경 시 디바운스 자동 저장.
  useEffect(() => {
    if (!data) return;
    latestDataRef.current = data;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }
    setSaveStatus('dirty');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const snapshot = latestDataRef.current;
      if (!snapshot) return;
      setSaveStatus('saving');
      try {
        await saveRef.current(snapshot);
        setSaveStatus('saved');
      } catch (e) {
        setSaveStatus('error');
        setSnack({ msg: e instanceof Error ? e.message : '자동 저장 실패', severity: 'error' });
      }
    }, DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [data]);

  // 언마운트 시 마지막 변경 flush.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        const snapshot = latestDataRef.current;
        if (snapshot) saveRef.current(snapshot).catch(() => {});
      }
    };
  }, []);

  const documents = useMemo(() => data?.documents ?? [], [data]);

  const setDocuments = useCallback((next: SpreadsheetDocument[]) => {
    setData((prev) => (prev ? { ...prev, documents: next } : prev));
  }, []);

  const handleFolderImport = useCallback((imported: SpreadsheetDocument[]) => {
    // 읽는 동안의 편집을 보존하고, 일괄 추가한 문서는 접어 파일 목록부터 보여준다.
    setData((prev) =>
      prev ? { ...prev, documents: appendImportedDocuments(prev.documents, imported) } : prev,
    );
  }, []);

  const updateDoc = useCallback((docId: string, patch: Partial<SpreadsheetDocument>) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            documents: prev.documents.map((d) => (d.id === docId ? { ...d, ...patch } : d)),
          }
        : prev,
    );
  }, []);

  const handleAddBlank = useCallback(() => {
    const doc = createEmptyDocument(`${newDocName} ${documents.length + 1}`, defaultColumns);
    setDocuments([...documents, doc]);
    setExpanded((prev) => new Set(prev).add(doc.id));
    setEditingTitleId(doc.id);
  }, [documents, defaultColumns, newDocName, setDocuments]);

  const handleDeleteDoc = useCallback(
    (docId: string) => {
      const doc = documents.find((d) => d.id === docId);
      if (!doc) return;
      if (!window.confirm(`'${doc.name}' 문서를 삭제할까요? 되돌릴 수 없습니다.`)) return;
      setDocuments(documents.filter((d) => d.id !== docId));
    },
    [documents, setDocuments],
  );

  const handleExportDoc = useCallback(
    async (doc: SpreadsheetDocument) => {
      try {
        if (exportDoc) await exportDoc(doc);
        else await exportDocumentToXlsx(doc, exportPrefix);
      } catch (e) {
        setSnack({ msg: e instanceof Error ? e.message : '엑셀 내보내기 실패', severity: 'error' });
      }
    },
    [exportPrefix, exportDoc],
  );

  // 엑셀 파싱 결과를 동작에 맞게 반영.
  const applySheets = useCallback(
    (
      action: UploadAction,
      workbook: import('xlsx').WorkBook,
      sheetNames: string[],
      fileBase: string,
      multiSheet: boolean,
    ) => {
      const { sheets, issues } = readWorkbookSheets(workbook, sheetNames, importGrid);
      setSheetIssues(issues);
      if (!sheets.length) {
        setSnack({ msg: '가져온 시트가 없습니다. 시트별 사유를 확인하세요.', severity: 'warning' });
        return;
      }
      const skipped = issues.length ? ` · ${issues.length}개 시트 건너뜀` : '';
      const totalRows = sheets.reduce((count, sheet) => count + sheet.grid.dataRows.length, 0);
      if (action.mode === 'new') {
        const imported = sheets.map(({ sheetName, grid }) =>
          documentFromGrid(multiSheet ? `${fileBase} (${sheetName})` : fileBase, grid),
        );
        setData((prev) =>
          prev ? { ...prev, documents: appendImportedDocuments(prev.documents, imported) } : prev,
        );
        // 여러 시트는 목록부터 보여주어 필요한 문서만 펼치도록 한다.
        if (sheetNames.length === 1) setExpanded((prev) => new Set(prev).add(imported[0].id));
        setSnack({
          msg: `문서 ${imported.length}개 추가 (${totalRows}행)${skipped}`,
          severity: issues.length ? 'warning' : 'success',
        });
      } else {
        const target = latestDataRef.current?.documents.find((d) => d.id === action.docId);
        if (!target) return;
        const document = sheets.reduce(
          (doc, sheet) => appendGridToDocument(doc, sheet.grid).document,
          target,
        );
        const addedColumns = document.columns.length - target.columns.length;
        updateDoc(document.id, document);
        setSnack({
          msg: `${sheets.length}개 시트에서 ${totalRows}행 추가${addedColumns ? `, 컬럼 ${addedColumns}개 신설` : ''}${skipped}`,
          severity: issues.length ? 'warning' : 'success',
        });
      }
    },
    [importGrid, updateDoc],
  );

  const triggerUpload = useCallback((action: UploadAction) => {
    uploadActionRef.current = action;
    fileInputRef.current?.click();
  }, []);

  /**
   * 고른 파일을 전부 가져온다. 파일마다 시트를 물으면 수십 번 확인해야 하므로
   * 모든 시트를 가져오고, 실패한 시트/파일만 사유와 함께 알린다.
   */
  const applyFiles = useCallback(
    async (action: UploadAction, files: File[]) => {
      setImporting(true);
      setSheetIssues([]);
      setFileProgress({ completed: 0, total: files.length });
      try {
        const { sheets, issues } = await importWorkbookFiles(files, {
          importGrid,
          onProgress: (completed, total) => setFileProgress({ completed, total }),
        });
        setFileIssues(issues);
        if (!sheets.length) {
          setSnack({
            msg: `가져온 시트가 없습니다. ${issues.length}건의 사유를 확인하세요.`,
            severity: 'error',
          });
          return;
        }
        const totalRows = sheets.reduce((count, sheet) => count + sheet.grid.dataRows.length, 0);
        const skipped = issues.length ? ` · ${issues.length}건 건너뜀` : '';
        if (action.mode === 'new') {
          const imported = sheets.map((sheet) =>
            documentFromGrid(sheetDocumentName(sheet), sheet.grid),
          );
          setData((prev) =>
            prev ? { ...prev, documents: appendImportedDocuments(prev.documents, imported) } : prev,
          );
          setSnack({
            msg: `파일 ${files.length}개에서 문서 ${imported.length}개 추가 (${totalRows}행)${skipped}`,
            severity: issues.length ? 'warning' : 'success',
          });
          return;
        }
        const target = latestDataRef.current?.documents.find((d) => d.id === action.docId);
        if (!target) return;
        const document = sheets.reduce(
          (doc, sheet) => appendGridToDocument(doc, sheet.grid).document,
          target,
        );
        const addedColumns = document.columns.length - target.columns.length;
        updateDoc(document.id, document);
        setSnack({
          msg: `파일 ${files.length}개에서 ${totalRows}행 추가${addedColumns ? `, 컬럼 ${addedColumns}개 신설` : ''}${skipped}`,
          severity: issues.length ? 'warning' : 'success',
        });
      } finally {
        setImporting(false);
        setFileProgress(null);
      }
    },
    [importGrid, updateDoc],
  );

  const handleFilePick = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length === 0) return;
      const action = uploadActionRef.current;
      // 파일 하나면 기존 흐름 그대로 — 여러 시트일 때 어떤 시트를 쓸지 고를 수 있다.
      if (files.length > 1) {
        setFileIssues([]);
        await applyFiles(action, files);
        return;
      }
      const file = files[0];
      const base = fileBaseName(file.name);
      setImporting(true);
      setSheetIssues([]);
      setFileIssues([]);
      try {
        const parsed = await parseExcelWorkbook(file);
        if (parsed.sheetNames.length === 0) {
          setSnack({ msg: '시트가 없는 파일입니다.', severity: 'error' });
          return;
        }
        if (parsed.sheetNames.length === 1) {
          applySheets(action, parsed.workbook, parsed.sheetNames, base, false);
          return;
        }
        setPendingWorkbook({
          action,
          workbook: parsed.workbook,
          sheetNames: parsed.sheetNames,
          fileBase: base,
        });
        setPendingSheetName(parsed.sheetNames[0]);
      } catch (err) {
        setSnack({ msg: err instanceof Error ? err.message : '파일 읽기 실패', severity: 'error' });
      } finally {
        setImporting(false);
      }
    },
    [applySheets, applyFiles],
  );

  const handleConfirmSheet = useCallback(
    (allSheets = false) => {
      if (!pendingWorkbook || !pendingSheetName) return;
      applySheets(
        pendingWorkbook.action,
        pendingWorkbook.workbook,
        allSheets ? pendingWorkbook.sheetNames : [pendingSheetName],
        pendingWorkbook.fileBase,
        true,
      );
      setPendingWorkbook(null);
      setPendingSheetName('');
    },
    [pendingWorkbook, pendingSheetName, applySheets],
  );

  const handleManualSave = useCallback(async () => {
    const snapshot = latestDataRef.current;
    if (!snapshot) return;
    setSaveStatus('saving');
    try {
      await saveRef.current(snapshot);
      setSaveStatus('saved');
      setSnack({ msg: '저장되었습니다.', severity: 'success' });
    } catch (e) {
      setSaveStatus('error');
      setSnack({ msg: e instanceof Error ? e.message : '저장 실패', severity: 'error' });
    }
  }, []);

  const toggleExpand = useCallback((docId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  const statusBadge = useMemo(() => {
    switch (saveStatus) {
      case 'dirty':
        return {
          label: '입력 중...',
          color: 'text.secondary' as const,
          icon: <EditIcon fontSize="inherit" />,
        };
      case 'saving':
        return {
          label: '저장 중...',
          color: 'text.secondary' as const,
          icon: <SaveIcon fontSize="inherit" />,
        };
      case 'saved':
        return {
          label: '저장됨',
          color: 'success.main' as const,
          icon: <CheckIcon fontSize="inherit" />,
        };
      case 'error':
        return { label: '저장 실패', color: 'error.main' as const, icon: null };
      default:
        return null;
    }
  }, [saveStatus]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Chip size="small" variant="outlined" label={`문서 ${documents.length}개`} />
        {statusBadge && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ color: statusBadge.color }}
          >
            {statusBadge.icon && (
              <Box sx={{ display: 'flex', fontSize: 14 }}>{statusBadge.icon}</Box>
            )}
            <Typography variant="caption" fontWeight={500}>
              {statusBadge.label}
            </Typography>
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        {exportAllDocs && (
          <SpreadsheetBatchDownload
            documents={documents}
            disabled={loading || !data || importing || folderImporting || !!pendingWorkbook}
            exportDocuments={exportAllDocs}
          />
        )}
        {writable && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              aria-label="엑셀/CSV 파일 선택"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              style={{ display: 'none' }}
              onChange={handleFilePick}
            />
            <Button
              variant="outlined"
              startIcon={<NoteAddIcon />}
              onClick={handleAddBlank}
              disabled={!data}
            >
              빈 문서
            </Button>
            <Button
              variant="contained"
              startIcon={
                importing ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />
              }
              onClick={() => triggerUpload({ mode: 'new' })}
              disabled={!data || importing || folderImporting}
            >
              {fileProgress && fileProgress.total > 1
                ? `업로드 중 ${fileProgress.completed}/${fileProgress.total}`
                : '엑셀/CSV 업로드'}
            </Button>
            {allowFolderUpload && (
              <FolderSpreadsheetUpload
                disabled={!data || loading || importing || !!pendingWorkbook}
                importGrid={importGrid}
                allSheets={allowAllSheetsImport}
                onImport={handleFolderImport}
                onBusyChange={setFolderImporting}
              />
            )}
            <Tooltip title="지금 저장">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleManualSave}
                  disabled={!data || saveStatus === 'saving'}
                  aria-label="저장"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {sheetIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSheetIssues([])}>
          가져오지 못한 시트 {sheetIssues.length}개
          <Box
            component="ul"
            sx={{ my: 1, pl: 2, maxHeight: 180, overflow: 'auto', overflowWrap: 'anywhere' }}
          >
            {sheetIssues.map((issue) => (
              <li key={issue.sheetName}>
                {issue.sheetName}: {issue.message}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {fileIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFileIssues([])}>
          가져오지 못한 항목 {fileIssues.length}개
          <Box
            component="ul"
            sx={{ my: 1, pl: 2, maxHeight: 180, overflow: 'auto', overflowWrap: 'anywhere' }}
          >
            {fileIssues.map((issue) => (
              <li key={`${issue.fileName}/${issue.sheetName ?? ''}`}>
                {issue.fileName}
                {issue.sheetName ? ` (${issue.sheetName})` : ''}: {issue.message}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            문서가 없습니다.
            {writable ? ' "엑셀/CSV 업로드" 로 파일을 올리거나 "빈 문서" 로 시작하세요.' : ''}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {documents.map((doc, index) => (
            <Paper
              key={doc.id}
              variant="outlined"
              onDragOver={(event) => {
                if (
                  !writable ||
                  !allowReorder ||
                  !draggedId.current ||
                  draggedId.current === doc.id
                )
                  return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                const rect = event.currentTarget.getBoundingClientRect();
                setDropTarget({ id: doc.id, after: event.clientY >= rect.top + rect.height / 2 });
              }}
              onDrop={(event) => {
                if (!draggedId.current || !writable || !allowReorder) return;
                event.preventDefault();
                const rect = event.currentTarget.getBoundingClientRect();
                moveDocument(
                  draggedId.current,
                  0,
                  doc.id,
                  event.clientY >= rect.top + rect.height / 2,
                );
                draggedId.current = null;
                setDropTarget(null);
              }}
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                borderTopColor:
                  dropTarget?.id === doc.id && !dropTarget.after ? 'primary.main' : undefined,
                borderBottomColor:
                  dropTarget?.id === doc.id && dropTarget.after ? 'primary.main' : undefined,
                boxShadow:
                  dropTarget?.id === doc.id
                    ? `0 ${dropTarget.after ? 3 : -3}px 0 #3b82f6`
                    : undefined,
              }}
            >
              {/* 헤더는 button 이 아니라 Box — 내부 액션 IconButton 과 중첩 button 충돌 방지. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '44px minmax(0, 1fr)',
                    sm: '28px minmax(0, 1fr) auto',
                  },
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'action.hover',
                  '& .MuiIconButton-root': {
                    minWidth: { xs: 44, sm: 28 },
                    minHeight: { xs: 44, sm: 28 },
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => toggleExpand(doc.id)}
                  aria-label={expanded.has(doc.id) ? `${doc.name} 접기` : `${doc.name} 펼치기`}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transition: 'transform 150ms',
                      transform: expanded.has(doc.id) ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                  />
                </IconButton>
                {writable && editingTitleId === doc.id ? (
                  <TextField
                    autoFocus
                    size="small"
                    value={doc.name}
                    onChange={(e) => updateDoc(doc.id, { name: e.target.value })}
                    onBlur={() => setEditingTitleId(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        e.preventDefault();
                        setEditingTitleId(null);
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    variant="standard"
                    sx={{ minWidth: 0, mr: 1, '& input': { fontSize: 15, fontWeight: 700 } }}
                  />
                ) : (
                  <Typography
                    fontWeight={700}
                    onClick={() => toggleExpand(doc.id)}
                    onDoubleClick={() => writable && setEditingTitleId(doc.id)}
                    sx={{
                      minWidth: 0,
                      mr: 1,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      overflowWrap: 'anywhere',
                    }}
                    title={writable ? '더블 클릭으로 제목 변경' : undefined}
                  >
                    {doc.name || <em style={{ opacity: 0.5 }}>(제목 없음)</em>}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    gridColumn: { xs: '1 / -1', sm: '3' },
                  }}
                >
                  {writable && allowReorder && (
                    <DocumentOrderControl
                      name={doc.name}
                      index={index}
                      count={documents.length}
                      onMove={(position) => moveDocument(doc.id, position)}
                      onDragStart={(event) => {
                        draggedId.current = doc.id;
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', doc.id);
                      }}
                      onDragEnd={() => {
                        draggedId.current = null;
                        setDropTarget(null);
                      }}
                    />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${doc.columns.length}컬럼 · ${doc.rows.length}행`}
                    sx={{ mr: { xs: 'auto', sm: 0.5 }, flexShrink: 0 }}
                  />
                  {writable && (
                    <Tooltip title="제목 수정">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingTitleId(doc.id);
                          setExpanded((prev) => new Set(prev).add(doc.id));
                        }}
                        aria-label={`${doc.name} 제목 수정`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="엑셀로 내보내기">
                    <IconButton
                      size="small"
                      onClick={() => handleExportDoc(doc)}
                      aria-label={`${doc.name} 엑셀 내보내기`}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {renderDocActions?.(doc)}
                  {writable && (
                    <>
                      <Tooltip title="이 문서에 엑셀 추가(행 append)">
                        <IconButton
                          size="small"
                          onClick={() => triggerUpload({ mode: 'append', docId: doc.id })}
                          disabled={importing || folderImporting}
                          aria-label={`${doc.name} 엑셀 추가`}
                        >
                          <UploadFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="문서 삭제">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDoc(doc.id)}
                          aria-label={`${doc.name} 삭제`}
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
              <Collapse in={expanded.has(doc.id)} unmountOnExit>
                <Box sx={{ p: 2, pt: 1 }}>
                  <SpreadsheetGrid
                    columns={doc.columns}
                    rows={doc.rows}
                    writable={writable}
                    stampColumnName={stampColumnName}
                    onColumnsChange={(cols: SpreadsheetColumn[]) =>
                      updateDoc(doc.id, { columns: cols })
                    }
                    onRowsChange={(rws: SpreadsheetRow[]) => updateDoc(doc.id, { rows: rws })}
                  />
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Stack>
      )}

      {/* 다중 시트 선택. */}
      <WorkbookSheetDialog
        open={!!pendingWorkbook}
        fileBase={pendingWorkbook?.fileBase ?? ''}
        sheetNames={pendingWorkbook?.sheetNames ?? []}
        selectedSheet={pendingSheetName}
        append={pendingWorkbook?.action.mode === 'append'}
        onSelect={setPendingSheetName}
        onClose={() => setPendingWorkbook(null)}
        onConfirm={() => handleConfirmSheet()}
        onImportAll={allowAllSheetsImport ? () => handleConfirmSheet(true) : undefined}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Paper>
  );
};

export default SpreadsheetDocuments;
